import { useParams, Navigate, Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { ExchangeForm } from '../components/Exchange/ExchangeForm';
import { PopularPairs, FaqList, CtaSection } from '../components/Sections';
import { FEATURED_COINS } from '../lib/content';
import { buildTitle, SITE } from '../lib/site';
import { useI18n } from '../i18n';

const baseSym = (t: string) => t.split('_')[0].toUpperCase();

function metaFor(from: string, to: string) {
  const fromCoin = FEATURED_COINS.find((c) => c.ticker === from);
  const toCoin = FEATURED_COINS.find((c) => c.ticker === to);
  const fromName = fromCoin?.name ?? baseSym(from);
  const toName = toCoin?.name ?? baseSym(to);
  return {
    from, to, fromName, toName,
    fromSymbol: fromCoin?.symbol ?? baseSym(from),
    toSymbol: toCoin?.symbol ?? baseSym(to),
  };
}

export function ExchangePairPage() {
  const { locale: t, fmt } = useI18n();
  const p = t.pages.exchangePair;
  const params = useParams();
  const slug = params.pair as string | undefined;
  if (!slug || !slug.includes('-to-')) return <Navigate to="/" replace />;
  const [from, to] = slug.split('-to-');
  if (!from || !to) return <Navigate to="/" replace />;

  const meta = metaFor(from, to);
  const title = `${meta.fromSymbol} → ${meta.toSymbol}`;
  const aboutBody = fmt(p.aboutBody, {
    fromName: meta.fromName,
    toName: meta.toName,
    from: meta.fromSymbol,
    to: meta.toSymbol,
  });

  return (
    <>
      <Seo
        title={buildTitle(title)}
        description={aboutBody}
        path={`/exchange/${slug}`}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: `${meta.fromSymbol} to ${meta.toSymbol} exchange on ${SITE.name}`,
            description: aboutBody,
            provider: { '@type': 'Organization', name: SITE.name, url: SITE.url },
            areaServed: 'Worldwide',
            serviceType: 'Cryptocurrency exchange',
          }),
        }}
      />

      <section className="hero" style={{ paddingBottom: 40 }}>
        <div className="container">
          <div className="hero-grid">
            <div className="hero-copy">
              <span className="eyebrow fade-up">{p.eyebrow}</span>
              <h1 className="fade-up d1" style={{ fontSize: 'clamp(32px, 5vw, 54px)' }}>
                {meta.fromSymbol} <span className="accent">→</span> {meta.toSymbol}
              </h1>
              <p className="lead fade-up d2">{aboutBody}</p>
              <div className="hero-cta fade-up d3">
                <Link to="/coins" className="btn btn-outline btn-large">{t.footer.supportedCoins}</Link>
                <Link to="/fees" className="btn btn-ghost btn-large">{t.footer.feesRates}</Link>
              </div>
            </div>

            <div className="hero-widget">
              <div className="widget-glow" />
              <div className="exchange-widget-card">
                <ExchangeForm initialFrom={from} initialTo={to} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container container-narrow">
          <div className="prose">
            <h2>{fmt(p.aboutTitle, { from: meta.fromSymbol, to: meta.toSymbol })}</h2>
            <p>{aboutBody}</p>
            <div className="callout">
              <div className="title">{p.beforeTitle}</div>
              <p>{p.beforeDesc}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: 'linear-gradient(180deg, transparent, rgba(245,181,68,0.02), transparent)' }}>
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">{p.moreEyebrow}</span>
            <h2>{p.moreTitle}</h2>
          </div>
          <PopularPairs limit={6} />
        </div>
      </section>

      <section className="section">
        <div className="container container-narrow">
          <div className="section-head center">
            <span className="eyebrow">{p.faqEyebrow}</span>
            <h2>{p.faqTitle}</h2>
          </div>
          <FaqList limit={4} />
        </div>
      </section>

      <CtaSection />
    </>
  );
}
